import { Calendar, ExternalLink } from "lucide-react";

// Booking platform configurations with logos/colors
const bookingPlatforms = {
  opentable: {
    name: "OpenTable",
    color: "bg-red-600 hover:bg-red-700",
    logo: "https://cdn.otstatic.com/cfe/14/images/opentable-logo-153e80.svg"
  },
  thefork: {
    name: "TheFork",
    color: "bg-green-600 hover:bg-green-700",
    logo: null
  },
  booksy: {
    name: "Booksy",
    color: "bg-purple-600 hover:bg-purple-700",
    logo: null
  },
  booking_ninja: {
    name: "Booking Ninja",
    color: "bg-blue-600 hover:bg-blue-700",
    logo: null
  },
  google_reserve: {
    name: "Reserve with Google",
    color: "bg-blue-500 hover:bg-blue-600",
    logo: null
  },
  resy: {
    name: "Resy",
    color: "bg-amber-600 hover:bg-amber-700",
    logo: null
  },
  yelp_reservations: {
    name: "Yelp Reservations",
    color: "bg-red-500 hover:bg-red-600",
    logo: null
  },
  custom: {
    name: "Book Now",
    color: "bg-brand hover:bg-brand-dark",
    logo: null
  }
};

export default function BookingLinks({ booking, externalBooking, website, isPremium }) {
  // Only show for premium venues
  if (!isPremium) return null;
  
  // Collect all available booking links
  const availableLinks = [];
  
  // Check external booking - new format (platform + booking_url)
  if (externalBooking?.platform && externalBooking?.booking_url) {
    const platformConfig = bookingPlatforms[externalBooking.platform] || bookingPlatforms.custom;
    availableLinks.push({
      platform: externalBooking.platform,
      url: externalBooking.booking_url,
      ...platformConfig
    });
  }
  
  // Legacy format: Check external booking links (old format with multiple URLs)
  if (externalBooking && !externalBooking.platform) {
    Object.entries(externalBooking).forEach(([platform, url]) => {
      if (url && bookingPlatforms[platform] && platform !== 'booking_url') {
        availableLinks.push({
          platform,
          url,
          ...bookingPlatforms[platform]
        });
      }
    });
  }
  
  // Check traditional booking
  if (booking?.booking_url) {
    availableLinks.push({
      platform: "direct",
      url: booking.booking_url,
      name: "Reserve a Table",
      color: "bg-brand hover:bg-brand-dark"
    });
  }
  
  // If no booking links but has website, offer that
  if (availableLinks.length === 0 && website) {
    availableLinks.push({
      platform: "website",
      url: website,
      name: "Visit Website",
      color: "bg-gray-600 hover:bg-gray-700"
    });
  }
  
  if (availableLinks.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-brand" />
        Reservations & Booking
      </h3>
      
      <div className="space-y-2">
        {availableLinks.map((link) => (
          <a
            key={link.platform}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center justify-between px-4 py-3 rounded-xl text-white font-medium transition-colors ${link.color}`}
            data-testid={`booking-link-${link.platform}`}
          >
            <span className="flex items-center gap-2">
              {link.logo ? (
                <img src={link.logo} alt={link.name} className="h-5 w-auto" />
              ) : (
                <Calendar className="w-5 h-5" />
              )}
              {link.name}
            </span>
            <ExternalLink className="w-4 h-4" />
          </a>
        ))}
      </div>
      
      {booking?.accepts_reservations && (
        <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          This venue accepts reservations
        </p>
      )}
    </div>
  );
}
